import { useState } from 'react';
import type { Vessel, TankReading } from '../types';
import { calculateLPGWeight } from '../lib/calculations';
import { ArrowRight, Flame } from 'lucide-react';

interface LPGInputProps {
  vessel: Vessel;
  onCalculate: (result: {
    totalWeight: number;
    details: string[];
    warnings: string[];
    tank_readings: TankReading[];
  }) => void;
}

export default function LPGInput({ vessel, onCalculate }: LPGInputProps) {
  const [readings, setReadings] = useState<TankReading[]>(
    vessel.tank_tables?.map((t) => ({
      tank_no: t.tank_no,
      ullage: 0,
      temp: -42,
      observed_density: 0.51,
    })) || []
  );
  

  const update = (i: number, field: keyof TankReading, val: string) => {
    const num = val === '' ? 0 : parseFloat(val);
    setReadings((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: num } : r)));
  };

  const handleCalc = () => {
    const result = calculateLPGWeight(readings, vessel);
    onCalculate({ ...result, tank_readings: readings });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="glass rounded-xl p-4 flex items-center gap-3 border-orange-500/20">
        <Flame className="w-5 h-5 text-orange-400" />
        <div className="text-sm">
          <span className="text-navy-300 font-medium">{vessel.name}</span>
          <span className="text-navy-500 mx-2">•</span>
          <span className="text-orange-400 font-medium">LPG Carrier</span>
        </div>
      </div>

      {/* Mobile-friendly card layout */}
      <div className="space-y-3">
        {readings.map((r, i) => (
          <div key={r.tank_no} className="card-premium p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-navy-100 text-sm">{r.tank_no}</span>
              <span className="text-xs text-navy-500">
                {vessel.tank_tables?.find(t => t.tank_no === r.tank_no)?.name || ''}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] text-navy-500 mb-1">
                  Ullage <span className="text-navy-600">(m)</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  className="input-premium py-2 text-sm"
                  value={r.ullage || ''}
                  onChange={(e) => update(i, 'ullage', e.target.value)}
                  placeholder="0.000"
                />
              </div>
              <div>
                <label className="block text-[10px] text-navy-500 mb-1">
                  Temp <span className="text-navy-600">(°C)</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  className="input-premium py-2 text-sm"
                  value={r.temp || ''}
                  onChange={(e) => update(i, 'temp', e.target.value)}
                  placeholder="-42.0"
                />
              </div>
              <div>
                <label className="block text-[10px] text-navy-500 mb-1">
                  Density <span className="text-navy-600">(t/m³)</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  className="input-premium py-2 text-sm"
                  value={r.observed_density || ''}
                  onChange={(e) => update(i, 'observed_density', e.target.value)}
                  placeholder="0.510"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={handleCalc} className="w-full py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-xl font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-2">
        Calculate LPG Weight <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
